import { useEffect, useState } from 'react';
import { getAllActsAPI } from '~entities/recital-manager/api/get-all-acts';
import { getAllPerformancesAPI } from '~entities/recital-manager/api/get-all-performances';
import type { Act, Performance, Show } from '~entities/recital-manager/model/recital';

interface ShowWithPerformances extends Show {
  performances?: Array<Performance>;
}

export const useShowsWithPerformances = (shows: Array<Show>) => {
  const [showsWithPerformances, setShowsWithPerformances] = useState<Array<ShowWithPerformances>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchShowsWithPerformances = async () => {
      if (shows.length === 0) {
        setShowsWithPerformances([]);
        return;
      }

      setLoading(true);
      
      try {
        // Fetch all acts for all shows in parallel
        const actsPromises = shows.map(show => 
          getAllActsAPI({ showId: show._id }).catch((): Promise<Array<Act>> => {
            return Promise.resolve([]);
          })
        );
        
        const actsResults = await Promise.all(actsPromises);
        
        // Extract all act IDs to fetch performances
        const allActs: Array<Act> = actsResults.flatMap(result => result);
        const actIds: Array<string> = allActs.map((act: Act) => act._id);
        
        // Fetch all performances for all acts in parallel
        let performancesResults: Array<Array<Performance>> = [];
        if (actIds.length > 0) {
          const performancesPromises = actIds.map(actId => 
            getAllPerformancesAPI({ actId }).catch((): Promise<Array<Performance>> => {
              return Promise.resolve([]);
            })
          );
          
          performancesResults = await Promise.all(performancesPromises);
        }
        
        // Create a map of actId to performances
        const actPerformancesMap = new Map<string, Array<Performance>>();
        actIds.forEach((actId, index) => {
          const performances = performancesResults[index] ?? [];
          actPerformancesMap.set(actId, performances);
        });
        
        // Create a map of showId to acts
        const showActsMap = new Map<string, Array<Act>>();
        shows.forEach((show, index) => {
          const acts = actsResults[index] ?? [];
          showActsMap.set(show._id, acts);
        });
        
// Build shows with performances
        const showsWithPerfs: Array<ShowWithPerformances> = shows.map(show => {
          const acts = showActsMap.get(show._id) ?? [];
          const performances = acts.flatMap(act => actPerformancesMap.get(act._id) ?? []);
          
          return {
            ...show,
            performances,
          };
        });
        
        setShowsWithPerformances(showsWithPerfs);
      } catch (error) {
        // Fallback to shows without performances
        const fallback = shows.map(show => ({
          ...show,
          performances: [],
        }));
        setShowsWithPerformances(fallback);
      } finally {
        setLoading(false);
      }
    };

    void fetchShowsWithPerformances();
  }, [shows]);

  return { showsWithPerformances, loading };
};